/**
 * humanRhythmCoordinator.js — FASE R6 coordinator.
 *
 * Assembles all R6 signals into a compact rhythm context string
 * for injection into the system prompt.
 *
 * Output philosophy:
 *   "Sin cierre completo. Deja espacio."       ✓
 *   "Varía el inicio. Respuesta corta."         ✓
 *   "Human variability module active."          ✗
 *   "Apply understatement due to grief signal." ✗
 *
 * Max output: 100 chars. No block headers. Natural language only.
 * Signal-gated: returns null when no rhythm correction is needed.
 */

import { buildRhythmProfile }        from "./conversationalRhythmEngine";
import { resolveEmotionalBreathing } from "./emotionalBreathingSystem";
import { resolveUnderstatement }     from "./silenceAndPauseResolver";
import { detectCadenceRepetition }   from "./pastoralCadenceVariation";

const MAX_RHYTHM_CHARS = 100;

const LOGISTIC_INTENTS = new Set(["planning", "logistics", "analysis", "events"]);

/**
 * @param {{
 *   emotion:             string,
 *   exhaustionState:     string,
 *   capacity:            string,
 *   riskLevel:           string,
 *   processState:        string|null,
 *   intent:              string,
 *   sessionLoad:         string,
 *   conversationLength:  number,
 *   historyMsgs:         Array,
 *   minimalismLevel:     "high"|"moderate"|"low",
 * }} params
 * @returns {{
 *   rhythmContext:   string|null,
 *   rhythmProfile:   string,
 *   understateMode:  boolean,
 * }}
 */
export function buildHumanRhythmContext({
  emotion            = "neutral",
  exhaustionState    = "estable",
  capacity           = "stable",
  riskLevel          = "none",
  processState       = null,
  intent             = "general",
  sessionLoad        = "low",
  conversationLength = 0,
  historyMsgs        = [],
  minimalismLevel    = "low",
}) {
  // Logistics/planning — rhythm shaping actively counterproductive here
  if (LOGISTIC_INTENTS.has(intent)) {
    return { rhythmContext: null, rhythmProfile: "functional", understateMode: false };
  }

  const rhythm     = buildRhythmProfile({ messageCount: conversationLength, emotionalWeight: emotion, sessionLoad, intent });
  const breathing  = resolveEmotionalBreathing({ emotion, exhaustionState, capacity, riskLevel, processState });
  const understate = resolveUnderstatement({ emotion, processState, conversationLength, exhaustionState });
  const cadence    = detectCadenceRepetition(historyMsgs);

  const parts = [];

  // Priority 1: understatement hint (grief/exhaustion — most important to surface)
  if (understate.silenceHint) {
    parts.push(understate.silenceHint);
  }

  // Priority 2: breathing instruction (when understatement doesn't already cover it)
  if (breathing.instruction && !understate.silenceHint) {
    parts.push(breathing.instruction);
  }

  // Priority 3: cadence variation (anti-formula)
  // Suppressed when R7 minimalism gate is already high — don't pile on instructions
  if (cadence.variationNeeded && minimalismLevel !== "high") {
    const hasOpenerRepeat = cadence.avoidPatterns.some((p) => p.startsWith("opener:"));
    const hasLengthRepeat = cadence.avoidPatterns.some((p) => p.startsWith("length:"));
    const hasListRepeat   = cadence.avoidPatterns.includes("structure:list");

    if (hasOpenerRepeat && parts.length < 2) parts.push("Varía el inicio.");
    if (hasLengthRepeat && parts.length < 2) parts.push("Varía el largo.");
    if (hasListRepeat   && parts.length < 2) parts.push("Sin lista esta vez.");
  }

  if (parts.length === 0) {
    return { rhythmContext: null, rhythmProfile: rhythm.rhythmProfile, understateMode: understate.understateMode };
  }

  // Compose — take max 2 fragments, cap at MAX_RHYTHM_CHARS
  const raw          = parts.slice(0, 2).join(" ");
  const rhythmContext = raw.length > MAX_RHYTHM_CHARS
    ? raw.slice(0, MAX_RHYTHM_CHARS - 1) + "."
    : raw;

  return { rhythmContext, rhythmProfile: rhythm.rhythmProfile, understateMode: understate.understateMode };
}
